import Knock, { FeedItem } from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react-notification-feed';
import { KeyboardArrowUp, Settings } from '@material-ui/icons';
import { Role } from 'lib/generated/graphql';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import NotificationItem from 'src/screens/notifications/components/Notification';
import NotificationPreferenceItem from 'src/screens/notifications/components/NotificationPreferenceItem';
import SkillsAndCollaborators from 'src/screens/user/skills-collaborators';
import useAuth from 'src/shared-components/Auth';
import Button from 'src/shared-components/Button';
import PageContainer from 'src/shared-components/Layout/PageContainer';

interface NotificationsPageProps {}

// TODO: Pagination?

// TODO: Loading

interface NotificationPreferences {
  newProjectMatches: boolean;
  someoneInvitedJoins: boolean;
  projectSharedByInvitee: boolean;
  projectSharedByCollaborator: boolean;
  weeklyProjectsRundown: boolean;
  email: boolean;
  sms: boolean;
}

const NotificationsPage: FunctionComponent<NotificationsPageProps> = () => {
  const router = useRouter();
  const { isLoggedIn, user, refetch } = useAuth();
  const [notificationsSettingsOpen, setNotificationsSettingsOpen] = useState(true);
  const [knockClient, setKnockClient] = useState<Knock | null>(null);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    newProjectMatches: true,
    someoneInvitedJoins: true,
    projectSharedByInvitee: true,
    projectSharedByCollaborator: true,
    weeklyProjectsRundown: true,
    email: true,
    sms: user?.phoneNumber ? true : false || false,
  });

  const knockFeed = useKnockFeed();
  const items = knockFeed?.useFeedStore(state => state.items);
  const [newNotifications, setNewNotifications] = useState([]);
  const [oldNotifications, setOldNotifications] = useState([]);

  const skillsAndCollaboratorsRef = useRef<any>();

  if (!isLoggedIn) return null;

  useEffect(() => {
    if (items) {
      items.forEach(item => {
        if (!item.seen_at) {
          knockFeed?.feedClient.markAsSeen(item);
          knockFeed?.feedClient.markAsRead(item);
        }
      });
      items.forEach(item => {
        if (!item.seen_at) {
          setNewNotifications(prev => [...prev, item]);
        } else {
          setOldNotifications(prev => [...prev, item]);
        }
      });
    }
  }, [items]);

  useEffect(() => {
    (async () => {
      const knockClient = new Knock(knockFeed.knock.apiKey);
      // @ts-ignore
      knockClient.authenticate(knockFeed.knock.userId, knockFeed.knock.userToken);
      setKnockClient(knockClient);
      const knockPreferences = await knockClient.preferences.get();

      const newPreferences = { ...preferences } as NotificationPreferences;

      if (knockPreferences.channel_types !== null) {
        if ('sms' in knockPreferences.channel_types && user.phoneNumber) {
          newPreferences.sms = knockPreferences.channel_types.sms;
        }

        if ('email' in knockPreferences.channel_types) {
          newPreferences.email = knockPreferences.channel_types.email;
        }
      }

      if (knockPreferences.workflows !== null) {
        if ('builder-project-matches-skills-and-interests' in knockPreferences.workflows) {
          newPreferences.newProjectMatches =
            knockPreferences.workflows['builder-project-matches-skills-and-interests']['channel_types'].email;
        }

        if ('builder-invitee-joins' in knockPreferences.workflows) {
          newPreferences.someoneInvitedJoins =
            knockPreferences.workflows['builder-invitee-joins']['channel_types'].email;
        }

        if ('builder-project-shared-by-invitee' in knockPreferences.workflows) {
          newPreferences.projectSharedByInvitee =
            knockPreferences.workflows['builder-project-shared-by-invitee']['channel_types'].email;
        }

        if ('builder-project-shared-by-collaborator' in knockPreferences.workflows) {
          newPreferences.projectSharedByCollaborator =
            knockPreferences.workflows['builder-project-shared-by-collaborator']['channel_types'].email;
        }

        if ('builder-weekly-project-rundown' in knockPreferences.workflows) {
          newPreferences.projectSharedByCollaborator =
            knockPreferences.workflows['builder-weekly-project-rundown']['channel_types'].email;
        }
      }

      setPreferences(newPreferences);
    })();
  }, [user]);

  const toggleNotificationPreference = (key: string) => {
    global?.analytics?.track(`Set Notification Preference`, {
      key,
      value: !preferences[key],
    });
    switch (key) {
      case 'newProjectMatches':
        knockClient.preferences.setWorkflow('builder-project-matches-skills-and-interests', {
          channel_types: {
            email: !preferences.newProjectMatches,
            in_app_feed: !preferences.newProjectMatches,
            sms: !preferences.newProjectMatches,
          },
        });
        setPreferences({
          ...preferences,
          newProjectMatches: !preferences.newProjectMatches,
        });
        break;
      case 'someoneInvitedJoins':
        knockClient.preferences.setWorkflow('builder-invitee-joins', {
          channel_types: {
            email: !preferences.someoneInvitedJoins,
            in_app_feed: !preferences.someoneInvitedJoins,
            sms: !preferences.someoneInvitedJoins,
          },
        });
        setPreferences({
          ...preferences,
          someoneInvitedJoins: !preferences.someoneInvitedJoins,
        });
        break;
      case 'projectSharedByInvitee':
        knockClient.preferences.setWorkflow('builder-project-shared-by-invitee', {
          channel_types: {
            email: !preferences.projectSharedByInvitee,
            in_app_feed: !preferences.projectSharedByInvitee,
            sms: !preferences.projectSharedByInvitee,
          },
        });
        setPreferences({
          ...preferences,
          projectSharedByInvitee: !preferences.projectSharedByInvitee,
        });
        break;
      case 'projectSharedByCollaborator':
        knockClient.preferences.setWorkflow('builder-project-shared-by-collaborator', {
          channel_types: {
            email: !preferences.projectSharedByCollaborator,
            in_app_feed: !preferences.projectSharedByCollaborator,
            sms: !preferences.projectSharedByCollaborator,
          },
        });
        setPreferences({
          ...preferences,
          projectSharedByCollaborator: !preferences.projectSharedByCollaborator,
        });
        break;
      case 'weeklyProjectRundown':
        knockClient.preferences.setWorkflow('weekly-project-rundown', {
          channel_types: {
            email: !preferences.weeklyProjectsRundown,
            in_app_feed: !preferences.weeklyProjectsRundown,
            sms: !preferences.weeklyProjectsRundown,
          },
        });
        setPreferences({
          ...preferences,
          projectSharedByCollaborator: !preferences.projectSharedByCollaborator,
        });
        break;
      case 'email':
        knockClient.preferences.setChannelType('email', !preferences.email);
        setPreferences({
          ...preferences,
          email: !preferences.email,
        });
        break;
      case 'sms':
        knockClient.preferences.setChannelType('sms', !preferences.sms);
        setPreferences({
          ...preferences,
          sms: !preferences.sms,
        });
    }
  };

  return (
    <PageContainer title="Notifications">
      {user?.role === Role.Builder && (
        <>
          <div
            onClick={() => setNotificationsSettingsOpen(!notificationsSettingsOpen)}
            className="bg-[#EEEEEE] rounded-2xl mb-3 w-fit px-4 py-2 flex flex-row items-center gap-x-2 cursor-pointer"
          >
            <Settings fontSize="medium" className="text-[#A8A8A8]" aria-hidden="true" />
            <span>{notificationsSettingsOpen ? 'Hide' : 'Show'} Preferences</span>
            <div
              className={`transform transition-all ease-in-out duration-300 rotate-${
                notificationsSettingsOpen ? '0' : '180'
              }`}
            >
              <KeyboardArrowUp fontSize="medium" aria-hidden="true" />
            </div>
          </div>
          <div
            className={`mb-7 rounded-2xl border-[#BBBBBB] px-8 bg-white grid grid-rows-12 sm:grid-cols-12 transform transition-all ease-out duration-300 shadow ${
              notificationsSettingsOpen ? 'max-h-[1000px] border p-8' : 'max-h-0  border-0 p-0'
            }`}
          >
            {notificationsSettingsOpen && (
              <>
                <div className="col-span-7 border-[#DDDDDD] sm:border-r">
                  <p className="font-bold text-sm text-[#868686]">NOTIFY ME WHEN...</p>
                  <div className="flex flex-col gap-y-4 mt-4">
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('newProjectMatches')}
                      checked={preferences.newProjectMatches}
                      label={
                        <>
                          A new project meets my{' '}
                          <span
                            onClick={() => skillsAndCollaboratorsRef.current?.openSkillsModal()}
                            className="underline cursor-pointer"
                          >
                            Skills
                          </span>{' '}
                          and{' '}
                          <span
                            onClick={() => skillsAndCollaboratorsRef.current?.openSkillsModal()}
                            className="underline cursor-pointer"
                          >
                            Interests
                          </span>
                          .
                        </>
                      }
                    />
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('someoneInvitedJoins')}
                      checked={preferences.someoneInvitedJoins}
                      label="Someone I invited joins Huddle."
                    />
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('projectSharedByInvitee')}
                      checked={preferences.projectSharedByInvitee}
                      label="A project is shared by someone I've invited to Huddle."
                    />
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('projectSharedByCollaborator')}
                      checked={preferences.projectSharedByCollaborator}
                      label={
                        <>
                          A project is shared by one of my{' '}
                          <span
                            onClick={() => skillsAndCollaboratorsRef.current?.openCollaboratorsModal()}
                            className="underline"
                          >
                            Favorite Collaborators
                          </span>
                          .
                        </>
                      }
                    />
                  </div>
                </div>
                <div className="col-span-5 pt-8 sm:pt-0 sm:pl-8">
                  <p className="font-bold text-sm text-[#868686]">NOTIFY ME VIA...</p>
                  <div className="flex flex-col gap-y-4 mt-4 items-start">
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('email')}
                      checked={preferences.email}
                      label={'Email'}
                    />
                    <NotificationPreferenceItem
                      onClick={() => toggleNotificationPreference('sms')}
                      checked={preferences.sms}
                      label={'Text'}
                      disabled={!user?.phoneNumber}
                    />
                    {!user.phoneNumber && (
                      <Button onClick={() => router.push('/account/settings')} color="black">
                        Add Phone Number
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {oldNotifications.length === 0 && newNotifications.length === 0 ? (
        <div>You have no notifications yet</div>
      ) : (
        <>
          <ul>
            {newNotifications.map((notification: FeedItem) => (
              <NotificationItem key={notification.__cursor} notification={notification} />
            ))}
          </ul>
          {oldNotifications.length > 0 && (
            <>
              <h3 className="font-medium text-2xl mb-4">Past notifications</h3>
              <ul>
                {oldNotifications.map((notification: FeedItem) => (
                  <NotificationItem key={notification.__cursor} notification={notification} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <SkillsAndCollaborators
        ref={skillsAndCollaboratorsRef}
        user={user}
        onUserUpdated={async () => {
          await refetch();
        }}
      />
    </PageContainer>
  );
};

export default NotificationsPage;
